import { useEffect, useState } from 'react';
import { Save, AlertCircle, CheckCircle, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { getTikTokPixel, updateTikTokPixel } from '@/lib/api';

const MAX_PIXELS = 5;

export default function AdminTikTokPixel() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      const res = await getTikTokPixel();
      const data = res.data;
      
      if (!data.pixels || data.pixels.length === 0) {
        data.pixels = [];
      }
      
      setSettings(data);
    } catch (err) {
      toast.error('Не удалось загрузить настройки');
    } finally {
      setBusy(false);
    }
  };

  const persistDraft = async () => {
    setSaving(true);
    try {
      await updateTikTokPixel(settings);
      toast.success('Настройки TikTok Pixel сохранены');
    } catch (err) {
      toast.error('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const addPixel = () => {
    if (settings.pixels.length >= MAX_PIXELS) {
      toast.error(`Максимум ${MAX_PIXELS} пикселей`);
      return;
    }
    setSettings({
      ...settings,
      pixels: [...settings.pixels, { pixel_id: '', access_token: '', name: `TikTok ${settings.pixels.length + 1}` }]
    });
  };

  const removePixel = (index) => {
    setSettings({
      ...settings,
      pixels: settings.pixels.filter((_, i) => i !== index)
    });
  };

  const updatePixelField = (index, field, value) => {
    const newPixels = [...settings.pixels];
    newPixels[index] = { ...newPixels[index], [field]: value };
    setSettings({ ...settings, pixels: newPixels });
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!settings) return null;

  const configuredPixels = settings.pixels?.filter(p => p.pixel_id) || [];
  const isConfigured = configuredPixels.length > 0;

  return (
    <div className="space-y-6" data-testid="admin-tiktok-pixel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">TikTok Pixels</h1>
          <p className="text-slate-500">Настройка отслеживания TikTok (до {MAX_PIXELS} пикселей)</p>
        </div>
        <Button onClick={persistDraft} disabled={saving} data-testid="save-tiktok-pixel-button">
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Сохранение...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Сохранить
            </span>
          )}
        </Button>
      </div>

      {/* Status */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {settings.is_enabled && isConfigured ? (
              <>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-green-700">TikTok Pixels активны ({configuredPixels.length})</h3>
                  <p className="text-sm text-slate-500">События отправляются в TikTok</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-700">TikTok Pixels неактивны</h3>
                  <p className="text-sm text-slate-500">
                    {!isConfigured ? 'Добавьте и настройте хотя бы один пиксель' : 'Включите пиксели'}
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Global Enable Switch */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Включить TikTok Pixels</Label>
              <p className="text-sm text-slate-500">Активировать отправку событий в TikTok</p>
            </div>
            <Switch
              checked={settings.is_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              data-testid="tiktok-pixel-enabled-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Pixels List */}
      <div className="space-y-4">
        {settings.pixels?.map((pixel, index) => (
          <Card key={index}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${pixel.pixel_id ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <Input
                    value={pixel.name || ''}
                    onChange={(e) => updatePixelField(index, 'name', e.target.value)}
                    placeholder={`TikTok ${index + 1}`}
                    className="w-48 h-8 text-sm font-medium"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removePixel(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <div className="space-y-2">
                <Label>Pixel ID</Label>
                <Input
                  value={pixel.pixel_id || ''}
                  onChange={(e) => updatePixelField(index, 'pixel_id', e.target.value)}
                  placeholder="CXXXXXXXXXXXXXXXXX"
                  data-testid={`tiktok-pixel-id-input-${index}`}
                />
              </div>

              <div className="space-y-2">
                <Label>Access Token (Events API) — опционально</Label>
                <Input
                  type="password"
                  value={pixel.access_token || ''}
                  onChange={(e) => updatePixelField(index, 'access_token', e.target.value)}
                  placeholder="Для серверного трекинга"
                  data-testid={`tiktok-pixel-token-input-${index}`}
                />
                <p className="text-xs text-slate-500">Оставьте пустым для браузерного трекинга</p>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Add Pixel Button */}
        {settings.pixels?.length < MAX_PIXELS && (
          <Button
            variant="outline"
            className="w-full border-dashed"
            onClick={addPixel}
            data-testid="add-tiktok-pixel-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Добавить TikTok Pixel ({settings.pixels?.length || 0}/{MAX_PIXELS})
          </Button>
        )}
      </div>

      {/* Help */}
      <Card>
        <CardHeader>
          <CardTitle>Как получить Pixel ID?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal list-inside text-sm text-slate-600 space-y-2">
            <li>Зайдите в TikTok Ads Manager</li>
            <li>Перейдите в Assets → Events</li>
            <li>Выберите Web Events</li>
            <li>Создайте или выберите пиксель</li>
            <li>Скопируйте Pixel ID (начинается с C...)</li>
          </ol>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Отслеживаемые события: ViewContent (просмотр), CompleteRegistration (квиз), ClickButton (клик на мессенджер)
            </AlertDescription>
          </Alert>

          <Button variant="outline" asChild>
            <a
              href="https://ads.tiktok.com/marketing_api/docs?id=1739584855420929"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Документация TikTok Pixel
            </a>
          </Button>
        </CardContent>
      </Card>

      {/* Events Info */}
      <Card>
        <CardHeader>
          <CardTitle>Отслеживаемые события</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">ViewContent</p>
                <p className="text-sm text-slate-500">При загрузке страницы</p>
              </div>
              <span className="px-2 py-1 text-xs bg-pink-100 text-pink-700 rounded">Pixel</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">CompleteRegistration</p>
                <p className="text-sm text-slate-500">При завершении квиза</p>
              </div>
              <span className="px-2 py-1 text-xs bg-pink-100 text-pink-700 rounded">Pixel</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">ClickButton</p>
                <p className="text-sm text-slate-500">При клике на Telegram/WhatsApp</p>
              </div>
              <span className="px-2 py-1 text-xs bg-pink-100 text-pink-700 rounded">Pixel</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
